import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Users, Handshake } from "lucide-react";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { badgeStyle, accentColor } from "@/lib/badgeColor";

function scoreColor(s) {
  if (s == null) return "text-slate-400";
  if (s >= 75) return "text-emerald-700";
  if (s >= 50) return "text-amber-700";
  return "text-slate-600";
}

function CountPopover({ assetId, kind, count, icon: Icon, label, clientType }) {
  const [open, setOpen] = useState(false);
  const { data = [], isLoading } = useQuery({
    queryKey: ["pipeline-detail", kind, assetId, clientType],
    queryFn: async () => (await api.get(`/pipeline/assets/${assetId}/${kind}`, { params: (kind === "clients" && clientType) ? { client_type: clientType } : {} })).data,
    enabled: open && count > 0,
  });

  const row = (
    <span className="flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5 text-slate-400" />
      <span className="text-[11px] text-slate-500">{label}</span>
      <span className={`font-mono-num text-sm font-semibold ${count > 0 ? "text-slate-900 bg-slate-100 px-1.5 rounded" : "text-slate-400"}`}>
        {count > 0 ? count : "—"}
      </span>
    </span>
  );

  if (count === 0) {
    return <div className="flex items-center" data-testid={`pipeline-${kind}-count-${assetId}`}>{row}</div>;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="flex items-center hover:opacity-80"
          data-testid={`pipeline-${kind}-trigger-${assetId}`}
        >
          {row}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0" onClick={(e) => e.stopPropagation()} data-testid={`pipeline-${kind}-popover`}>
        <div className="px-3 py-2 border-b border-slate-100 text-[11px] font-semibold uppercase tracking-wider text-slate-600">{label} · {count}</div>
        <div className="max-h-60 overflow-y-auto divide-y divide-slate-100">
          {isLoading ? (
            <div className="p-3 text-sm text-slate-400">Loading…</div>
          ) : data.map((d, i) => (
            <div key={i} className="px-3 py-2 flex items-center justify-between gap-2">
              <span className="text-sm text-slate-800 truncate">{d.name}</span>
              <span className="text-[10px] font-medium text-slate-500 shrink-0">{d.offer_status || "—"}</span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function AssetCard({ a, clientType }) {
  const navigate = useNavigate();
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/assets/${a.id}`)}
      onKeyDown={(e) => { if (e.key === "Enter") navigate(`/assets/${a.id}`); }}
      className="cursor-pointer bg-white border border-slate-200 rounded-md p-3 shadow-sm hover:shadow-md transition-shadow"
      data-testid={`pipeline-asset-card-${a.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900 truncate" data-testid="pipeline-asset-name">{a.name}</div>
          <div className="font-mono-num text-[10px] text-slate-500">{a.asset_code}</div>
        </div>
        <div className="text-right shrink-0">
          <div className={`font-mono-num text-lg font-bold leading-none ${scoreColor(a.best_match_score)}`} data-testid="pipeline-match-score">
            {a.best_match_score == null ? "—" : `${a.best_match_score}%`}
          </div>
          <div className="text-[9px] uppercase tracking-wider text-slate-400 mt-0.5">Match</div>
        </div>
      </div>
      <div className="mt-2">
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border" style={badgeStyle(a.badge_color)}>
          {a.status}
        </span>
      </div>
      <div className="mt-2.5 flex flex-col gap-1.5">
        <CountPopover assetId={a.id} kind="clients" count={a.offered_to_count} icon={Users} label="Offered To" clientType={clientType} />
        <CountPopover assetId={a.id} kind="brokers" count={a.brokers_offered_count} icon={Handshake} label="Brokers Offered" />
      </div>
    </div>
  );
}

export default function Kanban({ scope = "individual" }) {
  const isClient = scope === "institutional";
  const clientType = isClient ? "institutional" : "individual";
  const { data, isLoading } = useQuery({
    queryKey: ["pipeline-board", clientType],
    queryFn: async () => (await api.get("/pipeline/board", { params: { client_type: clientType } })).data,
  });

  const columns = useMemo(() => data?.columns || [], [data]);
  const byStatus = useMemo(() => {
    const map = {};
    for (const c of columns) map[c.label] = [];
    for (const a of (data?.assets || [])) {
      if (!map[a.status]) map[a.status] = [];
      map[a.status].push(a);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((x, y) => (y.offer_price_inr || 0) - (x.offer_price_inr || 0));
    }
    return map;
  }, [columns, data]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 pb-12">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Pipeline</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mt-1" data-testid="pipeline-title">{isClient ? "Client Pipeline" : "Lead Pipeline"}</h1>
          <p className="text-sm text-slate-600 mt-1.5">{isClient ? "Assets offered to institutional clients, by lifecycle status. Highest value first." : "Assets offered to individual leads, by lifecycle status. Highest value first."}</p>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto scrollbar-thin">
        <div className="flex gap-3 min-w-max pb-4" data-testid="kanban-board">
          {isLoading ? (
            ASSET_SKELETON.map((s) => (
              <div key={s} className="w-72 shrink-0">
                <div className="label-eyebrow mb-2">&nbsp;</div>
                <Skeleton className="h-32 w-full" />
              </div>
            ))
          ) : columns.map((col) => {
            const cards = byStatus[col.label] || [];
            return (
              <div key={col.label} className="w-72 shrink-0 bg-slate-100 rounded-md p-2" data-testid={`kanban-column-${col.label}`}>
                <div className="flex items-center justify-between px-1 py-1.5">
                  <div className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: accentColor(col.badge_color) }} />
                    <div className="label-eyebrow">{col.label}</div>
                  </div>
                  <span className="font-mono-num text-[10px] font-semibold text-slate-500 bg-white px-1.5 py-0.5 rounded">
                    {cards.length}
                  </span>
                </div>
                <div className="space-y-2">
                  {cards.length === 0 ? (
                    <div className="text-[11px] text-slate-400 text-center py-6" data-testid={`kanban-empty-${col.label}`}>
                      No assets
                    </div>
                  ) : cards.map((a) => <AssetCard key={a.id} a={a} clientType={clientType} />)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const ASSET_SKELETON = [1, 2, 3, 4, 5, 6, 7];
